<template>
  <div
    v-if="recommendations.length && !isPreorder && !isGiftCardOrder"
    class="mini-cart__recommendation js-vue-minicart-recommendation"
  >
    <LoadingIndicator v-if="miniCartRecIsLoading" class="mini-cart__recommendation-loader" />
    <div v-else class="mini-cart__recommendation-container">
      <a class="mini-cart__recommendation-image-container" :href="productUrl">
        <ImageContentful
          ref="recImage"
          class="mini-cart__recommendation-image"
          :class="{ 'mini-cart__recommendation-image--shown': hasLoadedRecImage }"
          :src="imageUrl"
          :options="{
            imageCompression: 61,
            fixedWidth: imageSize,
            fixedHeight: imageSize,
            format: 'jpg',
            backgroundColor,
          }"
          :alt="recommendedProduct.name"
        />
      </a>
      <div class="mini-cart__recommendation-description">
        <a class="mini-cart__recommendation-title" :href="productUrl">{{
          recommendedProduct.name
        }}</a>
        <div class="mini-cart__recommendation-color">{{ recommendedVariant.colorName }}</div>
        <div class="mini-cart__recommendation-price">{{ recommendationPrice }}</div>
      </div>
      <QuickAdd
        :variant="recommendedVariant"
        :modifier="MODIFIER.miniCart"
        :context="$CONTEXTS.miniCart"
        @select-size="handleSelectSize"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { scrollElementTo } from '@/helpers/scroll'
import { format } from '@/helpers/currency-formatter'
import ImageContentful from '@/components/_shared/ImageContentful.vue'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import QuickAdd from '@/components/_shared/QuickAdd/QuickAdd.vue'
import { MODIFIER } from '@/components/_shared/QuickAdd/QuickAddButton.vue'
import { backgroundColor, imageSize } from './mini-cart-rec-settings.scss'

export default {
  name: 'MiniCartRec',
  components: { QuickAdd, LoadingIndicator, ImageContentful },

  data() {
    return {
      backgroundColor,
      hasLoadedRecImage: true,
      imageSize: parseFloat(imageSize),
      MODIFIER,
    }
  },

  computed: {
    ...mapState('store', ['currency']),
    ...mapGetters({
      recommendations: 'recommendation/miniCartItems',
      skus: 'recommendation/miniCartSkus',
      orderIsInitialized: 'order/dataIsInitialized',
      orderItems: 'order/items',
      isPreorder: 'order/isPreorder',
      isGiftCardOrder: 'order/isGiftCardOrder',
      miniCartRecIsLoading: 'recommendation/miniCartRecIsLoading',
      initialisedByDy: 'recommendation/initialisedByDy',
    }),

    recommendedProduct() {
      return this.recommendations[0] !== undefined ? this.recommendations[0] : {}
    },

    recommendedVariant() {
      const product = this.recommendedProduct
      if (product.variants === undefined || product.variants.length < 1) return {}

      const variantIndex =
        typeof product.displayedVariantId === 'number'
          ? product.variants.findIndex(variant => variant.id === product.displayedVariantId)
          : 0

      return product.variants[variantIndex] || product.variants[0]
    },

    recommendationPrice() {
      const amount = Number(this.recommendedProduct.price)
      return format(amount, this.currency)
    },

    imageUrl() {
      return this.recommendedVariant.imageUrl !== undefined ? this.recommendedVariant.imageUrl : ''
    },

    productUrl() {
      return this.recommendedVariant.productUrl
    },
  },

  watch: {
    orderIsInitialized() {
      if (this.orderItems.length) this.fetchData()
    },

    skus: {
      immediate: true,
      handler() {
        this.fetchData()
      },
    },

    async miniCartRecIsLoading() {
      if (this.miniCartRecIsLoading) {
        this.hasLoadedRecImage = false
      } else {
        await this.$nextTick()
        const img = this.$refs.recImage
        if (img) imagesLoaded(img, () => (this.hasLoadedRecImage = true))
      }
    },
  },

  created() {
    window.addEventListener('on:filter-page-add-to-cart', this.loadNewRecsIfNeeded)
    this.loadNewRecsIfNeeded()
  },

  beforeDestroy() {
    window.removeEventListener('on:filter-page-add-to-cart', this.loadNewRecsIfNeeded)
  },

  methods: {
    ...mapActions({
      fetchData: 'recommendation/fetchData',
    }),

    loadNewRecsIfNeeded() {
      // Covers the case where DY is blocked or no experiment is setup
      if (!this.initialisedByDy) this.fetchData()
    },

    handleSelectSize() {
      const scrollingElement = document.querySelector('.mini-cart__container--filled')
      scrollElementTo(scrollingElement, 0)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './mini-cart-rec-settings';

.mini-cart {
  &__recommendation {
    padding: $site-gutter;
    text-align: center;
    background-color: $background-color;

    @media ($mq-high) {
      margin-bottom: 0;
      padding: 28px;
    }
  }

  &__recommendation-container {
    display: flex;
    align-items: center;
    flex: 1;
  }

  &__recommendation-description {
    flex: 1;
    text-align: left;
  }

  &__recommendation-image-container {
    width: $image-size;
    height: $image-size;
    margin-right: $site-gutter;
  }

  &__recommendation-image {
    opacity: 0;
    transition: opacity $transition-time;

    &--shown {
      opacity: 1;
    }
  }

  &__recommendation-title {
    display: inline-block;
    margin-bottom: 2px;
    color: $color-text;
    letter-spacing: $font-spacing-small;
  }

  &__recommendation-color,
  &__recommendation-price {
    font-size: $font-size-small;
    line-height: space(5, $font-size-small);
    letter-spacing: $font-spacing-small;
    color: $color-grey-mid-3;
  }

  &__recommendation-loader {
    width: $loader-size;
    height: $loader-size;
    margin: #{($image-size - $loader-size) / 2} auto;
  }
}
</style>
